import React, { useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusCircleIcon,
  EyeIcon,
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  ClipboardIcon,
  ArrowTopRightOnSquareIcon,
  DocumentArrowDownIcon,
  MinusIcon,
} from "@heroicons/react/24/outline";
import {
  ComponentEnum,
  ComponentI,
  isSupportedComponentType,
  LessonI,
} from "../../../types";
import { useNotification } from "../../../hooks/useNotification";
import Button from "../../../componentLibrary/Button";
import {
  SelectedComponentType,
  handleCopyComponent,
} from "../utils/selectedComponentUtils";
import { CopyVariableButton } from "../utils/CopyVariableButton";
import {
  lessonHasUnsupportedComponents,
  getLessonWithUnsupportedRemoved,
} from "../utils/unsupportedComponents";
import { isQuillContentEmpty } from "../../../utils/quillUtils";
import PreviewLessonModal from "./PreviewLessonModal";
import AddVariableModal from "./AddVariableModal";
import UnsupportedComponentsSaveModal from "./UnsupportedComponentsSaveModal";

export function validateComponent(component: ComponentI, pageId: string) {
  const message: { text: string; pageId: string; componentId: string }[] = [];
  const content = component.content;
  let multipleChoiceError = false;
  let priorityChoiceError = false;
  switch (component.type) {
    case ComponentEnum.IMAGE:
      if (!content.imageId) {
        message.push({
          text: "Please add an image for the image component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.MULTIPLE_CHOICE:
      if (
        component.content.linkedVariable?.length === 0 &&
        !component.content.choice_1
      ) {
        message.push({
          text: "Please add at least one choice or linked variable to the multiple choice component.",
          pageId,
          componentId: component._id,
        });
      }
      Object.entries(content).forEach(([key, val]) => {
        if (!key.startsWith("other") && !key.startsWith("msq") && !val) {
          multipleChoiceError = true;
        }
      });

      if (multipleChoiceError) {
        message.push({
          text: "Please fill in the label, variable, and all choices for the multiple choice component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.RATING_SCALE:
      if (!content.label?.trim() || !content.variable?.trim()) {
        message.push({
          text: "Please add both a label and variable name for the rating scale component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.TEXT:
      if (isQuillContentEmpty(content.text)) {
        message.push({
          text: "Please add some text content to the text component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.VIDEO:
      if (!content.url.trim() && isQuillContentEmpty(content.transcript)) {
        message.push({
          text: "Please add either a URL or transcript for the video component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.INPUT:
      if (!content.variable.trim()) {
        message.push({
          text: "Please add a variable name for the input component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.TOGGLE_TEXT:
      if (!content.title.trim()) {
        message.push({
          text: "Please add a title for the toggle text component.",
          pageId,
          componentId: component._id,
        });
      } else if (isQuillContentEmpty(content.body)) {
        message.push({
          text: "Please add content to the body of the toggle text component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.LOGIC_COMPONENT:
      if (
        content.display === null ||
        content.display === undefined ||
        content.display === ""
      ) {
        message.push({
          text: "Please choose whether to display or hide the logic component.",
          pageId,
          componentId: component._id,
        });
      }
      if (
        Array.isArray(content.componentsList) &&
        content.componentsList.length === 0
      ) {
        message.push({
          text: "Please add at least one component to the logic component's list.",
          pageId,
          componentId: component._id,
        });
      }
      if (Array.isArray(content.componentsList)) {
        for (const componentFromList of content.componentsList) {
          const nestedMessage = validateComponent(componentFromList, pageId);
          if (nestedMessage.length > 0) {
            message.push(...nestedMessage);
          }
        }
      }
      break;
    case ComponentEnum.LESSON_LINK:
      if (!content.module_id) {
        message.push({
          text: "Please select a module for the lesson link component.",
          pageId,
          componentId: component._id,
        });
      }
      if (!content.lesson_id) {
        message.push({
          text: "Please select a lesson for the lesson link component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.USER_LIST:
      if (!content.label?.trim()) {
        message.push({
          text: "Please add a label for the user list component.",
          pageId,
          componentId: component._id,
        });
      }
      if (!content.variable?.trim()) {
        message.push({
          text: "Please add a variable name for the user list component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.CUSTOM_LINK:
      if (!content.button_text?.trim()) {
        message.push({
          text: "Please add button text for the custom link component.",
          pageId,
          componentId: component._id,
        });
      }
      if (!content.url?.trim()) {
        message.push({
          text: "Please add a URL for the custom link component.",
          pageId,
          componentId: component._id,
        });
      } else {
        if (
          content.url_type === "external" &&
          !content.url.match(/^https?:\/\/.+/)
        ) {
          message.push({
            text: "Please make sure the external URL starts with http:// or https://",
            pageId,
            componentId: component._id,
          });
        } else if (
          content.url_type === "local" &&
          !content.url.startsWith("/")
        ) {
          message.push({
            text: "Please make sure the local URL starts with /",
            pageId,
            componentId: component._id,
          });
        }
      }
      if (content.icon_url && !content.icon_alt?.trim()) {
        message.push({
          text: "Please add alt text for the icon in the custom link component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.ACTION_DETAIL:
      {
        if (!content.detail_type?.trim()) {
          message.push({
            text: "Please select a detail type for the action detail component.",
            pageId,
            componentId: component._id,
          });
        }
        if (content.linked_variables.length === 0) {
          message.push({
            text: "Please add at least one linked variable to the action detail component.",
            pageId,
            componentId: component._id,
          });
        }
        if (!content.label?.trim()) {
          message.push({
            text: "Please add a label for the action detail component.",
            pageId,
            componentId: component._id,
          });
        }
      }
      break;
    case ComponentEnum.DATE_TIME:
      if (!content.label?.trim()) {
        message.push({
          text: "Please add a label for the date time component.",
          pageId,
          componentId: component._id,
        });
      }
      if (!content.type) {
        message.push({
          text: "Please select a type for the date time component.",
          pageId,
          componentId: component._id,
        });
      }
      if (!content.variable) {
        message.push({
          text: "Please add a variable name for the date time component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.PRIORITY_LIST:
      if (!content.label?.trim()) {
        message.push({
          text: "Please add a label for the priority list component.",
          pageId,
          componentId: component._id,
        });
      }
      if (!content.variable?.trim()) {
        message.push({
          text: "Please add a variable name for the priority list component.",
          pageId,
          componentId: component._id,
        });
      }
      if (
        component.content.linkedVariable?.length === 0 &&
        !component.content.choice_1
      ) {
        message.push({
          text: "At least one, linked variable or choice is required for the priority list component.",
          pageId,
          componentId: component._id,
        });
      }
      Object.entries(content).forEach(([key, val]) => {
        if (key.startsWith("choice") && !val) {
          priorityChoiceError = true;
        }
      });
      if (priorityChoiceError) {
        message.push({
          text: "Please fill in the choices for the priority list component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.CALLOUT_BOX:
      if (isQuillContentEmpty(content.definition ?? "")) {
        message.push({
          text: "Please add content for the Callout Box component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.FOR_EACH:
      if (!content.parsingVariable?.trim()) {
        message.push({
          text: "Please add a variable name for the foreach component.",
          pageId,
          componentId: component._id,
        });
      }
      break;
    case ComponentEnum.TOGGLE:
      if (!content.title?.trim()) {
        message.push({
          text: "Please add a title for the toggle component.",
          pageId,
          componentId: component._id,
        });
      }
      if (
        Array.isArray(content.componentsList) &&
        content.componentsList.length === 0
      ) {
        message.push({
          text: "Please add at least one component to the toggle component's list.",
          pageId,
          componentId: component._id,
        });
      }
      // Validate nested components
      if (Array.isArray(content.componentsList)) {
        for (const componentFromList of content.componentsList) {
          const nestedMessage = validateComponent(componentFromList, pageId);
          if (nestedMessage.length > 0) {
            message.push(...nestedMessage);
          }
        }
      }
      break;
    case ComponentEnum.ADD_TO_CALENDAR:
      if (!content.eventTitle?.trim()) {
        message.push({
          text: "Please add an event title for the Add to Calendar component.",
          pageId,
          componentId: component._id,
        });
      }
      // Validate that if one time is provided, both should be provided
      if (
        (content.startTime?.trim() && !content.endTime?.trim()) ||
        (!content.startTime?.trim() && content.endTime?.trim())
      ) {
        message.push({
          text: "Please provide both start and end times, or leave both empty for the Add to Calendar component.",
          pageId,
          componentId: component._id,
        });
      }
      // Validate that end time is after start time
      if (content.startTime?.trim() && content.endTime?.trim()) {
        const [startHours, startMinutes] = content.startTime.split(":");
        const [endHours, endMinutes] = content.endTime.split(":");
        const startTotalMinutes =
          parseInt(startHours, 10) * 60 + parseInt(startMinutes, 10);
        const endTotalMinutes =
          parseInt(endHours, 10) * 60 + parseInt(endMinutes, 10);
        if (endTotalMinutes <= startTotalMinutes) {
          message.push({
            text: "End time must be after start time for the Add to Calendar component.",
            pageId,
            componentId: component._id,
          });
        }
      }
      break;
    default: {
      // DB may contain component types from other envs (e.g. stage-only); skip validation for unsupported types
      if (!isSupportedComponentType(component.type)) {
        break;
      }
      const _exhaustiveCheck: never = component.type;
      throw new Error(`Unhandled component type: ${_exhaustiveCheck}`);
    }
  }

  return message;
}

export function validateLesson(
  lesson: LessonI,
  errorCallback?: (
    lessonErrors: { text: string }[],
    totalPageErrors: {
      pageTitle: string;
      pageErrors: { text: string; pageId: string; componentId: string }[];
    }[],
  ) => void,
) {
  const lessonErrors: { text: string }[] = [];
  const totalPageErrors: {
    pageTitle: string;
    pageErrors: { text: string; pageId: string; componentId: string }[];
  }[] = [];

  if (!lesson.title.trim()) {
    lessonErrors.push({ text: "Please add a title for this lesson" });
  }

  if (!lesson.icon?.src) {
    lessonErrors.push({ text: "Please add an icon for this lesson" });
  }

  for (const page of lesson.pages) {
    const currentPageErrors: {
      text: string;
      pageId: string;
      componentId: string;
    }[] = [];

    if (!page.title.trim()) {
      currentPageErrors.push({
        text: `Please add a title for the page "${page.title || "Untitled"}"`,
        pageId: page._id,
        componentId: "",
      });
    }
    if (page.components.length === 0) {
      currentPageErrors.push({
        text: `Please add at least one component to the page "${page.title || "Untitled"}"`,
        pageId: page._id,
        componentId: "",
      });
    }
    for (const component of page.components) {
      const componentError = validateComponent(component, page._id);
      if (componentError.length > 0) {
        currentPageErrors.push(...componentError);
      }
    }
    if (currentPageErrors.length > 0) {
      totalPageErrors.push({
        pageTitle: page.title,
        pageErrors: currentPageErrors,
      });
    }
  }

  if (lessonErrors.length > 0 || totalPageErrors.length > 0) {
    errorCallback?.(lessonErrors, totalPageErrors);
    return false;
  }

  return true;
}

export default function SubmitAndPreviewButtons({
  lesson,
  mutation,
  linkedModules,
  currentPageId,
  handlePastePreview,
  selectedComponent,
  setLessonChanged,
  setCurrentPageId,
  handleBackFunction,
  onLessonFilteredForSave,
  onLessonErrorCallback,
  onViewInLesson,
  onMinimizeAll,
  onExpandAll,
  areAllMinimized,
}: {
  lesson: LessonI;
  mutation: UseMutationResult<
    unknown,
    Error,
    { lesson: LessonI; modules: string[] },
    unknown
  >;
  linkedModules: string[];
  currentPageId: string;
  setCurrentPageId: React.Dispatch<React.SetStateAction<string>>;
  handlePastePreview: () => void;
  selectedComponent: SelectedComponentType;
  setLessonChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  handleBackFunction?: () => void;
  onLessonFilteredForSave?: (lesson: LessonI) => void;
  onLessonErrorCallback?: (
    lessonErrors: { text: string }[],
    totalPageErrors: {
      pageTitle: string;
      pageErrors: { text: string; pageId: string; componentId: string }[];
    }[],
  ) => void;
  onViewInLesson?: () => void;
  onMinimizeAll?: () => void;
  onExpandAll?: () => void;
  areAllMinimized?: boolean;
}) {
  const { notify } = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUnsupportedModalOpen, setIsUnsupportedModalOpen] = useState(false);

  const handleSubmitLesson = () => {
    setLessonChanged?.(false);
    mutation.mutate({ lesson, modules: linkedModules });
  };

  const handleSubmitClick = () => {
    if (!validateLesson(lesson, onLessonErrorCallback)) return;
    if (lessonHasUnsupportedComponents(lesson)) {
      setIsUnsupportedModalOpen(true);
    } else {
      handleSubmitLesson();
    }
  };

  const handleUnsupportedSaveAsIs = () => {
    setIsUnsupportedModalOpen(false);
    handleSubmitLesson();
  };

  const handleUnsupportedDeleteAndSave = () => {
    const filteredLesson = getLessonWithUnsupportedRemoved(lesson);
    onLessonFilteredForSave?.(filteredLesson);
    setLessonChanged?.(false);
    mutation.mutate({ lesson: filteredLesson, modules: linkedModules });
    setIsUnsupportedModalOpen(false);
  };

  const [isInsertModalOpen, setisInsertModalOpen] = React.useState(false);
  const closeModal = () => setisInsertModalOpen(false);
  const openModal = () => setisInsertModalOpen(true);

  const currentPageIndex = lesson.pages.findIndex(
    (page) => page._id === currentPageId,
  );

  const ButtonGroup = ({
    label,
    buttons,
    className,
  }: {
    label: string;
    buttons: React.ReactNode[];
    className?: string;
  }) => {
    return (
      <div
        className={`border border-gray p-1 rounded-lg h-full min-w-0 ${className}`}
      >
        <p className="border-b-2 border-gray text-xs text-center mb-0.5 truncate min-w-0">
          {label}
        </p>
        <div className="flex flex-row gap-1 justify-start flex-wrap">
          {buttons}
        </div>
      </div>
    );
  };

  const handlePasteVariable = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    try {
      const clipboardText = await navigator.clipboard.readText();
      const activeElement = document.activeElement;

      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
      ) {
        activeElement.value = activeElement.value + clipboardText;
        const event = new Event("input", { bubbles: true });
        activeElement.dispatchEvent(event);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to read clipboard:", err);
    }
  };

  const WebView = ({ className }: { className?: string }) => (
    <div
      className={`flex flex-row justify-between gap-1 min-w-0 overflow-hidden ${className}`}
    >
      <div className="flex gap-1 items-center min-w-0 overflow-hidden">
        <div className="border border-gray p-1 rounded-lg h-full flex items-center">
          <Button
            layout="iconText"
            className="max-w-[120px]"
            variant="alternative"
            size="xxs"
            onClick={handleBackFunction}
          >
            <ArrowLeftIcon className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Back</span>
          </Button>
        </div>
        <ButtonGroup
          label={lesson.pages[currentPageIndex]?.title || "(Untitled)"}
          buttons={[
            <Button
              key="back-button-web"
              variant="secondary"
              size="xxs"
              onClick={() => {
                if (currentPageIndex > 0) {
                  setCurrentPageId(lesson.pages[currentPageIndex - 1]._id);
                }
              }}
              disabled={currentPageIndex <= 0}
              layout="iconText"
            >
              <ChevronLeftIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Back</span>
            </Button>,
            <Button
              key="next-button-web"
              size="xxs"
              onClick={() => {
                if (currentPageIndex < lesson.pages.length - 1) {
                  setCurrentPageId(lesson.pages[currentPageIndex + 1]._id);
                }
              }}
              disabled={currentPageIndex >= lesson.pages.length - 1}
              layout="iconText"
            >
              <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Next</span>
            </Button>,
          ]}
        />
      </div>
      <div className="flex flex-row gap-1 flex-shrink-0">
        <ButtonGroup
          label="Variable"
          className="w-fit shrink-0"
          buttons={[
            <CopyVariableButton
              key="copy-variable-button-web"
              type="icon-text"
              size="xxs"
              component={selectedComponent.component}
            />,
            <Button
              variant="dark"
              key="insert-variable-button-web"
              onClick={openModal}
              size="xxs"
              layout="iconText"
            >
              <PlusCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Insert</span>
            </Button>,
            <Button
              size="xxs"
              variant="dark"
              onMouseDown={handlePasteVariable}
              key="paste-variable-button-web"
              layout="iconText"
            >
              <ClipboardIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Paste</span>
            </Button>,
          ]}
        />
        <ButtonGroup
          label="Component"
          className="w-fit shrink-0"
          buttons={[
            ...(onMinimizeAll &&
            onExpandAll &&
            areAllMinimized !== undefined &&
            (lesson.pages[currentPageIndex]?.components?.length ?? 0) > 0
              ? [
                  <Button
                    variant="secondary"
                    onClick={areAllMinimized ? onExpandAll : onMinimizeAll}
                    size="xxs"
                    key="minimize-all-button-web"
                    layout="iconText"
                    title={
                      areAllMinimized
                        ? "Expand all components"
                        : "Minimize all components"
                    }
                  >
                    <>
                      {areAllMinimized ? (
                        <CropSquareIcon className="h-3.5 w-3.5 flex-shrink-0" />
                      ) : (
                        <MinusIcon className="h-3.5 w-3.5 flex-shrink-0" />
                      )}
                      <span>{areAllMinimized ? "Expand" : "Minimize"}</span>
                    </>
                  </Button>,
                ]
              : []),
            <Button
              variant="dark"
              onClick={() => handleCopyComponent(selectedComponent, notify)}
              size="xxs"
              key="copy-component-button-web"
              layout="iconText"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Copy</span>
            </Button>,
            <Button
              variant="dark"
              onClick={handlePastePreview}
              size="xxs"
              key="paste-component-button-web"
              layout="iconText"
            >
              <ClipboardIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Paste</span>
            </Button>,
          ]}
        />
        <ButtonGroup
          label="Lesson"
          className="w-fit shrink-0"
          buttons={[
            <Button
              variant="secondary"
              onClick={() => {
                if (validateLesson(lesson, onLessonErrorCallback))
                  setIsModalOpen(true);
              }}
              size="xxs"
              key="preview-lesson-button-web"
              layout="iconText"
            >
              <EyeIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Preview</span>
            </Button>,
            ...(onViewInLesson
              ? [
                  <Button
                    variant="secondary"
                    onClick={onViewInLesson}
                    size="xxs"
                    key="view-in-lesson-button-web"
                    title="View in Lesson"
                    layout="iconText"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>View</span>
                  </Button>,
                ]
              : []),
            <Button
              variant="dark"
              onClick={handleSubmitClick}
              size="xxs"
              key="submit-lesson-button-web"
              layout="iconText"
            >
              <DocumentArrowDownIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Save</span>
            </Button>,
          ]}
        />
        <PreviewLessonModal
          lesson={lesson}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          currentPageId={currentPageId}
        />
      </div>
    </div>
  );

  const MobileView = ({ className }: { className?: string }) => (
    <div className={`${className}`}>
      <div className="md:hidden mb-0.5 flex justify-center">
        <Button
          className="flex items-center"
          variant="alternative"
          size="sm"
          onClick={handleBackFunction}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Back to content management</span>
        </Button>
      </div>
      <div className="border border-gray p-1 rounded-lg flex-1 flex items-center justify-between text-sm mb-1">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (currentPageIndex > 0) {
              setCurrentPageId(lesson.pages[currentPageIndex - 1]._id);
            }
          }}
          disabled={currentPageIndex <= 0}
          className="flex items-center gap-1"
          title="Previous"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </Button>
        <div className="truncate px-0.5">
          Page: {lesson.pages[currentPageIndex]?.title || "(Untitled)"}
        </div>
        <Button
          size="sm"
          onClick={() => {
            if (currentPageIndex < lesson.pages.length - 1) {
              setCurrentPageId(lesson.pages[currentPageIndex + 1]._id);
            }
          }}
          disabled={currentPageIndex >= lesson.pages.length - 1}
          className="flex items-center gap-1"
          title="Next"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-row justify-between gap-1 mb-0.5 overflow-x-auto">
        <div className="border border-gray p-1 rounded-lg items-center hidden md:flex">
          <Button
            className="flex items-center max-w-[150px]"
            variant="alternative"
            size="sm"
            onClick={handleBackFunction}
          >
            <ArrowLeftIcon className="h-6 w-6" />
            <span>Back to content management</span>
          </Button>
        </div>
        <ButtonGroup
          label="Variable"
          className="w-fit shrink-0"
          buttons={[
            <CopyVariableButton
              type="icon"
              size="sm"
              component={selectedComponent.component}
              title="Copy"
              key="copy-variable-button-mobile"
            />,
            <Button.Square
              onMouseDown={handlePasteVariable}
              title="Paste"
              className="p-1"
              variant="dark"
              key="paste-variable-button-mobile"
            >
              <ClipboardIcon className="w-6 h-6" />
            </Button.Square>,
            <Button.Square
              onClick={openModal}
              title="Insert"
              className="p-1"
              variant="dark"
              key="insert-variable-button-mobile"
            >
              <PlusCircleIcon className="w-7 h-7" />
            </Button.Square>,
          ]}
        />
        <ButtonGroup
          label="Component"
          className="w-fit shrink-0"
          buttons={[
            ...(onMinimizeAll &&
            onExpandAll &&
            areAllMinimized !== undefined &&
            (lesson.pages[currentPageIndex]?.components?.length ?? 0) > 0
              ? [
                  <Button.Square
                    variant="secondary"
                    onClick={areAllMinimized ? onExpandAll : onMinimizeAll}
                    title={
                      areAllMinimized
                        ? "Expand all components"
                        : "Minimize all components"
                    }
                    key="minimize-all-button-mobile"
                    className="p-1"
                  >
                    {areAllMinimized ? (
                      <CropSquareIcon className="w-6 h-6" />
                    ) : (
                      <MinusIcon className="w-6 h-6" />
                    )}
                  </Button.Square>,
                ]
              : []),
            <Button.Square
              variant="dark"
              onClick={() => handleCopyComponent(selectedComponent, notify)}
              title="Copy"
              key="copy-component-button-mobile"
              className="p-1"
            >
              <ClipboardDocumentIcon className="w-6 h-6" />
            </Button.Square>,
            <Button.Square
              variant="dark"
              onClick={handlePastePreview}
              title="Paste"
              key="paste-component-button-mobile"
              className="p-1"
            >
              <ClipboardIcon className="w-6 h-6" />
            </Button.Square>,
          ]}
        />
        <ButtonGroup
          label="Lesson"
          className="w-fit shrink-0"
          buttons={[
            <Button.Square
              variant="secondary"
              className="p-1"
              onClick={() => {
                if (validateLesson(lesson, onLessonErrorCallback))
                  setIsModalOpen(true);
              }}
              title="Preview"
              key="preview-lesson-button-mobile"
            >
              <EyeIcon className="w-5 h-5" />
            </Button.Square>,
            ...(onViewInLesson
              ? [
                  <Button.Square
                    variant="secondary"
                    className="p-1"
                    onClick={onViewInLesson}
                    title="View in Lesson"
                    key="view-in-lesson-button-mobile"
                  >
                    <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                  </Button.Square>,
                ]
              : []),
            <Button.Square
              className="p-1"
              variant="dark"
              onClick={handleSubmitClick}
              title="Submit"
              key="submit-lesson-button-mobile"
            >
              <DocumentArrowDownIcon className="w-6 h-6" />
            </Button.Square>,
          ]}
        />
        <PreviewLessonModal
          lesson={lesson}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          currentPageId={currentPageId}
        />
      </div>
    </div>
  );

  return (
    <div>
      <WebView className="hidden lg:flex" />
      <MobileView className="lg:hidden" />
      <AddVariableModal
        lesson={lesson}
        currentPageId={currentPageId}
        isModalOpen={isInsertModalOpen}
        closeModal={closeModal}
      />
      <UnsupportedComponentsSaveModal
        isOpen={isUnsupportedModalOpen}
        onSaveAsIs={handleUnsupportedSaveAsIs}
        onDeleteAndSave={handleUnsupportedDeleteAndSave}
        onCancel={() => setIsUnsupportedModalOpen(false)}
      />
    </div>
  );
}
